import type {
  NotificationFeedResponse,
  NotificationPreferencesResponse,
  UpdateNotificationPreferencesRequest,
} from "@xop/dashboard-shared";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api } from "../api.js";

const PREFS_KEY = ["notifications", "prefs"] as const;
const FEED_KEY = ["notifications", "feed"] as const;

export function useNotificationPrefs(): UseQueryResult<
  NotificationPreferencesResponse,
  Error
> {
  return useQuery({
    queryKey: PREFS_KEY,
    queryFn: () =>
      api<NotificationPreferencesResponse>("/api/notifications/preferences"),
  });
}

export function useUpdateNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateNotificationPreferencesRequest) =>
      api<NotificationPreferencesResponse>("/api/notifications/preferences", {
        method: "PUT",
        body,
      }),
    onSuccess: (data) => qc.setQueryData(PREFS_KEY, data),
  });
}

export function useNotificationsFeed(
  page = 1,
): UseQueryResult<NotificationFeedResponse, Error> {
  return useQuery({
    queryKey: [...FEED_KEY, page],
    queryFn: () =>
      api<NotificationFeedResponse>(
        `/api/notifications?page=${page}&per_page=25`,
      ),
    // The SSE stream invalidates this on each event; the interval is a
    // safety net for a dropped stream.
    refetchInterval: 60_000,
    placeholderData: (prev) => prev,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api("/api/notifications/read", { method: "POST", body: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: FEED_KEY }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api("/api/notifications/read-all", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: FEED_KEY }),
  });
}

/** Dismiss (delete) a single notification from the bell. */
export function useDismissNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api("/api/notifications/dismiss", { method: "POST", body: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: FEED_KEY }),
  });
}

/** Clear the caller's entire bell in one shot ("Clear all"). */
export function useClearNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api("/api/notifications/clear", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: FEED_KEY }),
  });
}
